import { createLogic } from "redux-logic";
import * as Action from "./action";
import * as Types from "./types";
import _ from "lodash";
import debugLog, {LogLevel} from "../utils/debugLog";

const updateSegmentsLogic = createLogic({
  type: Types.GET_BUS_ETA,
  warnTimeout: 0,
  processOptions: {
    dispatchMultiple: true,
  },
  async process({ action, httpClient, getState }: any, dispatch, done) {
    const { routes, serviceType } = action.payload;

    let allRouteLocation: any[] = [];

    try {
      const routesRes = await httpClient.get(
        `${process.env.REACT_APP_SERVER_URL}/api/routesByIds?Ids=${_.map(
          routes,
          (route) => route.id
        )}`
      );

      const currentRouteTags = getState().main.routeTags;

      // Collect route IDs that pass the service type filter
      const filteredRouteIds: number[] = [];

      routesRes.data.forEach((route: any) => {
        // Find if this route belongs to a tag that has '(shuttle)' in its name
        const parentTag = currentRouteTags.find((tag: any) =>
          tag.routes.some((r: any) => r.routeId === route.id)
        );
        const isTagShuttle = parentTag ? /\(shuttle\)/i.test(parentTag.name) : false;
        const isRouteShuttle = /\(shuttle\)/i.test(route.name);
        
        const isShuttle = isRouteShuttle || isTagShuttle;
        const shouldInclude = serviceType === 'shuttle' ? isShuttle : !isShuttle;

        if (route.status === 'active' && shouldInclude) {
          allRouteLocation = [...allRouteLocation, ...route.routeLocations];
          filteredRouteIds.push(route.id);
        }
      });

      // Filter route tags to only include routes matching the service type
      const filteredRouteTags = currentRouteTags
        .map((tag: any) => ({
          ...tag,
          routes: tag.routes.filter((route: any) =>
            filteredRouteIds.includes(route.routeId)
          ),
        }))
        .filter((tag: any) => tag.routes.length > 0);

      if (!_.isEqual(currentRouteTags, filteredRouteTags)) {
        dispatch(Action.getRouteTagsSuccess(filteredRouteTags));
      }
      dispatch(Action.getAllRouteLocation(allRouteLocation));

      const uniqueLocations: any[] = [];
      allRouteLocation.forEach((location) => {
        const target = _.find(uniqueLocations, {
          locationId: location.locationId,
        });
        if (target) {
          target.routeIds = _.uniq([...target.routeIds, location.routeId]);
        } else {
          uniqueLocations.push({ ...location, routeIds: [location.routeId] });
        }
      });

      // add new origin of routeLocations from API (routesRes.data) to uniqueLocations
      const uniqueLocationsWithRouteLocations = uniqueLocations.map((uniqueLocation) => {
        // Find the route in routesRes.data that matches this uniqueLocation’s routeId
        const matchingRoute = routesRes.data.find(
          (route: any) => route.id === uniqueLocation.routeId
        );

        // If found, filter routeLocations to be unique by locationId
        const routeLocations = matchingRoute
          ? _.sortBy(_.uniqBy(matchingRoute.routeLocations, 'locationId'), 'order')
          : [];

        return {
          ...uniqueLocation,
          routeLocations,
        };
      });

      dispatch(Action.updateLocationList(uniqueLocationsWithRouteLocations));
    } catch (error) {
      debugLog(LogLevel.ERROR, 'Unable to get `updateSegmentsLogic()`. ', {
          error: error,
      });
      throw error;
    }

    done();
  },
});

const getAllEtaLogic = createLogic({
  type: Types.GET_ALL_ETA,
  warnTimeout: 0,
  processOptions: {
    dispatchMultiple: true,
  },
  async process({ action, httpClient }: any, dispatch, done) {
    const { data } = action.payload;

    try {
      // Fetch data
      var allEta: any = [];
      data.forEach(async (route: any) => {
        const response = await httpClient.get(
          `${process.env.REACT_APP_SERVER_URL}/api/directions/${route.id}`
        );

        if (response.status === 200 && response.data) {
          allEta.push(response.data);
        } else {
          console.warn(`Failed to fetch data for route ${route.id}`);
        }
      });

      dispatch(Action.getAllEtaSuccess(allEta));
    } catch (error) {
      debugLog(LogLevel.ERROR, 'Unable to get `getAllEtaLogic()`. ', {
          error: error,
      });
      console.error("Error fetching data:", error);
    }
    done();
  },
});

const getRouteTagsLogic = createLogic({
  type: Types.GET_ROUTE_TAGS,
  warnTimeout: 0,
  processOptions: {
    dispatchMultiple: true,
  },
  async process({ httpClient }: any, dispatch, done) {
    try {
      const tenantId = process.env.REACT_APP_TENANT_ID;
      const routeTags = await httpClient.get(
        `${process.env.REACT_APP_SERVER_URL}/api/RouteTags?Filter={"TenantId": ${tenantId}}&PageSize=100`
      );

      const tags = await httpClient.get(
        `${process.env.REACT_APP_SERVER_URL}/api/tags?Filter={"TenantId": ${tenantId}}&PageSize=100`
      );

      const mappedTags = await Promise.all(
        tags.data.map((tagItem: any) => {
          return {
            ...tagItem,
            routes: _.filter(routeTags.data, (o) => {
              return o.tagId === tagItem.id;
            }),
          };
        })
      );
      const reversMappedTags = mappedTags.reverse();

      dispatch(Action.getRouteTagsSuccess(reversMappedTags));
    } catch (error) {
      debugLog(LogLevel.ERROR, 'Unable to get `getRouteTagsLogic()`. ', {
          error: error,
      });
      throw error;
    }

    done();
  },
});

const setNtpLogic = createLogic({
  type: Types.SET_NTP,
  warnTimeout: 0,
  processOptions: {
    dispatchMultiple: true,
  },
  async process({ action }: any, dispatch, done) {
    const { data } = action.payload;

    dispatch(Action.setNtpSuccess(data));

    done();
  },
});

export default [updateSegmentsLogic, getRouteTagsLogic, getAllEtaLogic, setNtpLogic];
